import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { getQueue, staffStreamURL } from "src/api/client";
import type {
  PatientLinkedEvent,
  PositionUpdateEvent,
  QueueCreatedEvent,
  QueueEntry,
  QueueUpdatedEvent,
  StatusChangedEvent,
} from "src/api/types";
import { type SSEEvent, useSSEFetch } from "src/hooks/useSSEFetch";

const POLL_INTERVAL = 60_000;

interface QueueState {
  entries: QueueEntry[];
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: "loaded"; entries: QueueEntry[] }
  | { type: "error"; message: string }
  | { type: "entryCreated"; id: string }
  | { type: "entryChanged"; id: string; entry: Partial<QueueEntry> }
  | { type: "entryRemoved"; id: string }
  | { type: "estimatesChanged"; estimates: Map<string, number | null> };

function reducer(state: QueueState, action: Action): QueueState {
  switch (action.type) {
    case "loaded":
      return { entries: action.entries, loading: false, error: null };
    case "error":
      return { ...state, loading: false, error: action.message };
    case "entryCreated":
      // Signal that a new entry exists — full state will arrive on next poll
      return { ...state };
    case "entryChanged": {
      const entries = state.entries.map((e) =>
        e.id === action.id ? { ...e, ...action.entry } : e,
      );
      if (action.entry.status === "completed" || action.entry.status === "cancelled") {
        return { ...state, entries: entries.filter((e) => e.id !== action.id) };
      }
      return { ...state, entries };
    }
    case "entryRemoved":
      return { ...state, entries: state.entries.filter((e) => e.id !== action.id) };
    case "estimatesChanged":
      // The event lists every waiting entry, so one it omits is no longer waiting.
      return {
        ...state,
        entries: state.entries.map((e) => ({
          ...e,
          estimated_wait_minutes: action.estimates.get(e.id),
        })),
      };
  }
}

export function useQueue(token: string, onUnauthorized?: () => void, onShiftEvent?: () => void) {
  const [state, dispatch] = useReducer(reducer, { entries: [], loading: true, error: null });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token, onUnauthorized, onShiftEvent]);
  const onUnauthorizedRef = useRef(onUnauthorized);
  const onShiftEventRef = useRef(onShiftEvent);

  const fetchQueue = useCallback(async () => {
    try {
      const entries = await getQueue(tokenRef.current);
      dispatch({ type: "loaded", entries });
      setLastUpdated(new Date());
    } catch (err) {
      const message = (err as Error).message;
      if (message === "unauthorized") {
        onUnauthorizedRef.current?.();
        return;
      }
      dispatch({ type: "error", message });
    }
  }, []);

  useEffect(() => {
    // Initial load; fetchQueue only sets state after the request resolves.
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    void fetchQueue();
    const id = setInterval(() => void fetchQueue(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchQueue]);

  // Refetch on window focus
  useEffect(() => {
    const handler = () => void fetchQueue();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [fetchQueue]);

  const sseHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleSSEEvent = useCallback(
    (event: SSEEvent) => {
      switch (event.type) {
        case "queue_entry.created": {
          const data = JSON.parse(event.data) as QueueCreatedEvent;
          dispatch({ type: "entryCreated", id: data.id });
          void fetchQueue();
          setLastUpdated(new Date());
          break;
        }
        case "queue_entry.status_changed": {
          const data = JSON.parse(event.data) as StatusChangedEvent;
          dispatch({
            type: "entryChanged",
            id: data.id,
            entry: {
              status: data.status,
              cancellation_reason: data.cancellation_reason,
              cancellation_note: data.cancellation_note,
            },
          });
          setLastUpdated(new Date());
          break;
        }
        case "queue_entry.patient_linked": {
          const data = JSON.parse(event.data) as PatientLinkedEvent;
          dispatch({ type: "entryChanged", id: data.id, entry: { patient_id: data.patient_id } });
          setLastUpdated(new Date());
          break;
        }
        case "queue_entry.updated": {
          const data = JSON.parse(event.data) as QueueUpdatedEvent;
          // Only the fields the event carries: it never un-submits.
          const submitted: Partial<QueueEntry> = {};
          if (data.insurance_card_submitted_at) {
            submitted.insurance_card_submitted_at = data.insurance_card_submitted_at;
          }
          if (data.consents_submitted_at) {
            submitted.consents_submitted_at = data.consents_submitted_at;
          }
          dispatch({ type: "entryChanged", id: data.id, entry: submitted });
          setLastUpdated(new Date());
          break;
        }
        case "queue.position_update": {
          const data = JSON.parse(event.data) as PositionUpdateEvent;
          const estimates = new Map(data.entries.map((e) => [e.id, e.estimated_wait_minutes]));
          dispatch({ type: "estimatesChanged", estimates });
          break;
        }
        case "shift.opened":
        case "shift.closed": {
          void fetchQueue();
          onShiftEventRef.current?.();
          setLastUpdated(new Date());
          break;
        }
      }
    },
    [fetchQueue],
  );

  useSSEFetch(staffStreamURL(), sseHeaders, handleSSEEvent, Boolean(token));

  return { ...state, refetch: fetchQueue, lastUpdated };
}
